import { snapshotDetailSchema, snapshotDrillSchema, snapshotGoalSchema, snapshotPayloadSchema, } from "@healthos/shared";
import { z } from "zod";
import { GetBriefResponse } from "./brief.js";
import { calendarDateString } from "./calendar-date.js";
import { LogbookPillar } from "./logbook.js";
export const SnapshotQuery = z.object({
    /**
     * Builds the week as it stood at the end of this local day instead of now,
     * for the day picker. A past week is never sent to the model: it gets the
     * deterministic narrative, and nothing after that evening is read.
     */
    asOf: calendarDateString("asOf").optional(),
});
// The snapshot payload plus a transport-only flag: true when the fast path
// served the scaffold with the deterministic fallback narrative and a richer
// AI narrative is still composing off the request path. The client then fetches
// GET /api/me/snapshot/narrative and crossfades the composed prose in.
export const SnapshotResponse = snapshotPayloadSchema.extend({
    narrativePending: z.boolean().optional(),
});
export const SnapshotDetailResponse = snapshotDetailSchema;
// The goal catalog as the picker renders it: every outcome the member can
// choose, grouped by behaviour on the client. Never invented at the edge.
export const SnapshotGoalOption = z.object({
    outcomeId: z.string(),
    behaviour: z.enum(["sleep", "movement", "food", "stress", "connection"]),
    label: z.string(),
    supporting: z.string(),
});
export const SnapshotGoalResponse = z.object({
    goal: snapshotGoalSchema.nullable(),
    options: z.array(SnapshotGoalOption),
});
// One goal at a time: choosing supersedes the current one, renew extends its
// window, end closes it. Three verbs, one key.
export const UpdateSnapshotGoalRequest = z.discriminatedUnion("action", [
    z.object({ action: z.literal("choose"), outcomeId: z.string().min(1) }),
    z.object({ action: z.literal("renew") }),
    z.object({ action: z.literal("end") }),
]);
/**
 * The day picker's range read: one row per local calendar day from `from` to
 * `to` inclusive, in the member's own zone. Bounded so a month grid with its
 * leading and trailing weeks fits in one call and nothing can ask for a year.
 */
export const SNAPSHOT_CALENDAR_MAX_DAYS = 62;
export const SnapshotCalendarQuery = z.object({
    from: calendarDateString("from"),
    to: calendarDateString("to"),
});
export const SnapshotCalendarDay = z.object({
    date: calendarDateString("date"),
    /** The step seam's own total for the day. `null` when nothing synced. */
    steps: z.number().int().nonnegative().nullable(),
    /** The goal that applied that day, so a ring can be drawn against it. */
    targetSteps: z.number().int().positive().nullable(),
    /** `null` for a day with no goal result yet (no goal, or today in progress). */
    goalMet: z.boolean().nullable(),
    /** Declared a recovery day, so it bridges the streak instead of breaking it. */
    isRestDay: z.boolean(),
    /** Anything in the Logbook that day, logged or synced. */
    hasEntries: z.boolean(),
    /** The Weekly Brief composed that day, if one was. */
    briefId: z.string().nullable(),
});
export const SnapshotCalendarResponse = z.object({
    timezone: z.string(),
    /** The member's own today, so the client never guesses it from the device clock. */
    today: calendarDateString("today"),
    streakDays: z.number().int().nonnegative(),
    bestStreakDays: z.number().int().nonnegative(),
    /** Whether today is held as a rest day, so the flame can show frozen. */
    todayRestDay: z.boolean(),
    days: z.array(SnapshotCalendarDay),
});
export const SnapshotDayQuery = z.object({ date: calendarDateString("date") });
/** One reading for the day, already reduced to a single value by the Logbook series. */
export const SnapshotDayMetric = z.object({
    key: z.string(),
    pillar: LogbookPillar,
    label: z.string(),
    value: z.number(),
    unit: z.string().nullable(),
});
/** Last night in detail. Stages and efficiency are whatever the wearable reported, never estimated. */
export const SnapshotDaySleep = z.object({
    hours: z.number().nonnegative(),
    deepHours: z.number().nonnegative().nullable(),
    remHours: z.number().nonnegative().nullable(),
    lightHours: z.number().nonnegative().nullable(),
    awakeHours: z.number().nonnegative().nullable(),
    /** Time asleep over time in bed, 0 to 1. */
    efficiency: z.number().min(0).max(1).nullable(),
    latencyMinutes: z.number().nonnegative().nullable(),
    /** Stage samples across the night, the same shape as the sleep drill's, when the device sends them. */
    hypnogram: snapshotDrillSchema.shape.hypnogram,
});
export const SnapshotDayTrendKey = z.enum(["sleep", "hrv", "rhr", "steps"]);
/**
 * Seven days ending on the selected day, oldest first, so the card can draw
 * the week behind the day and the line moves as the member swipes.
 */
export const SnapshotDayTrend = z.object({
    key: SnapshotDayTrendKey,
    label: z.string(),
    unit: z.string().nullable(),
    points: z.array(z.object({ date: calendarDateString("date"), value: z.number().nullable() })),
    /** Mean of the days that have a value. `null` when none do. */
    average: z.number().nullable(),
});
export const SnapshotDayResponse = z.object({
    date: calendarDateString("date"),
    today: calendarDateString("today"),
    steps: z
        .object({
        steps: z.number().int().nonnegative(),
        targetSteps: z.number().int().positive().nullable(),
        goalMet: z.boolean().nullable(),
        distanceMeters: z.number().int().nonnegative().nullable(),
        isRestDay: z.boolean(),
    })
        .nullable(),
    /** Hours asleep for the night that ended this morning. */
    sleepHours: z.number().nonnegative().nullable(),
    sleep: SnapshotDaySleep.nullable(),
    /** Only the trends with at least one value in the seven days. */
    trends: z.array(SnapshotDayTrend),
    /** Every other reading with a value that day, steps and sleep excluded. */
    metrics: z.array(SnapshotDayMetric),
    /** How many Logbook entries landed that day. */
    entryCount: z.number().int().nonnegative(),
    brief: GetBriefResponse.nullable(),
});
//# sourceMappingURL=snapshot.js.map